<script setup lang="ts">
import { IModal } from '@inkline/inkline'
import ArrangeVisitForm from '../forms/ArrangeVisitForm.vue'
import Icon from '~/components/global/Icon.vue'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import useArrangeVisitModalStore from '~/stores/modals/useArrangeVisitModal'

defineProps<{
  estateData: EstateDetailInterface
}>()

const arrangeVisitModalStore = useArrangeVisitModalStore()
</script>

<template>
  <IModal
    :hide-on-click-outside="false"
    :name="arrangeVisitModalStore.id"
    :model-value="arrangeVisitModalStore.isOpened"
    @close="arrangeVisitModalStore.onClose()"
    @open="arrangeVisitModalStore.onOpen()"
  >
    <template #header>{{ $t('modals.arrangeVisit.title') }}</template>
    <template #close><Icon name="close" /></template>

    <div class="c-modal-body">
      <div class="c-modal-body__representative">
        <img
          v-if="estateData.contact_person.image"
          class="sc-image"
          :src="estateData.contact_person.image"
          :alt="estateData.contact_person.name"
        />

        <div class="sc-info">
          <strong>{{ estateData.contact_person.name }}</strong>

          <a
            v-if="estateData.contact_person.phone"
            class="c-link c-link"
            :href="`tel:${estateData.contact_person.phone}`"
            >{{ estateData.contact_person.phone }}</a
          >
        </div>
      </div>

      <p
        class="c-modal-body__text"
        v-html="
          $t('modals.arrangeVisit.text', {
            _0: '<strong>',
            _1: '</strong>',
          })
        "
      />

      <ArrangeVisitForm
        :estate-data="estateData"
        :modal-store="arrangeVisitModalStore"
      />
    </div>
  </IModal>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

:deep(.modal) {
  --modal--body--padding: 40px 115px 64px;

  @include breakpoint-down('sm') {
    --modal--body--padding: 36px 50px;
  }
}

.c-modal-body {
  max-width: 360px;
  margin: auto;

  &__representative {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-bottom: 32px;

    .sc-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
    }

    .sc-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 2px;
      font-size: 22px;
      line-height: 1.2;

      .c-link {
        font-size: 16px;
        font-weight: 600;
      }
    }
  }

  &__text {
    text-align: center;
    line-height: 1.28;
    margin-bottom: 32px;
    padding-top: 32px;
    border-top: 1px solid var(--color-border);
  }
}
</style>
